/**
 * まとめてメールの送信待ち（#268 1.5c-2、docs/98 §7）。左のメニュー「送付状」の一覧のタブ「送信待ち」の中身。
 *   送信待ち・送信中と、直近 7 日の送信済・送信失敗・取消（GET /mail-queue）。送信待ちは「取消」（POST /mail-queue/:id/cancel）、送信済は送付の記録へ「表示」。
 *   送るのは API の見張り（枠が空き次第 1通ずつ）＝この画面は見るだけと取消だけ。1分ごとに読み直す（人の操作でない＝PASSIVE）
 */
import { MAIL_QUEUE_MSG as M, MAIL_QUEUE_STATUS_LABEL, type MailQueueItem, type MailQueueList } from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { api, messageOf } from "../api.js";
import { t, useLabels } from "../labels.js";
import { PASSIVE } from "../lib/autoLogout.js";
import { fmtDateTimeShort } from "../lib/dates.js";
import { useNarrow } from "../lib/useNarrow.js";
import { currentView } from "../lib/view.js";
import { ErrorText, useToast } from "./ui.jsx";

export const MAIL_QUEUE_KEY = ["mail-queue"] as const;

export function useMailQueue(enabled: boolean) {
  return useQuery({
    queryKey: MAIL_QUEUE_KEY,
    queryFn: () => api<MailQueueList>("GET", "/mail-queue", undefined, PASSIVE),
    enabled,
    refetchInterval: 60_000,
    staleTime: 10_000,
    retry: false,
  });
}

/** タブの件数＝これから送るもの（送信待ち・送信中） */
export const openCountOf = (items: ReadonlyArray<MailQueueItem> | undefined) => (items ? items.filter((x) => x.status === "queued" || x.status === "sending").length : undefined);

function StatusCell({ x }: { x: MailQueueItem }) {
  if (x.status === "failed") return <span className="shu">{`${MAIL_QUEUE_STATUS_LABEL.failed}：${M.failedCode(x.lastCode ?? "")}`}</span>;
  if (x.status === "queued" || x.status === "sending") return <b>{MAIL_QUEUE_STATUS_LABEL[x.status]}</b>;
  return <span>{MAIL_QUEUE_STATUS_LABEL[x.status]}</span>;
}

const nextText = (x: MailQueueItem) =>
  x.status === "queued" ? `${fmtDateTimeShort(x.nextTryAt)}（${x.reason === "day" ? M.reasonDay : M.reasonRate}）` : x.status === "sent" && x.finishedAt ? fmtDateTimeShort(x.finishedAt) : "—";

export function MailQueuePanel() {
  useLabels(); // 言葉を変えたら描き直す
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const q = useMailQueue(true);
  const [busy, setBusy] = useState<string | null>(null);
  const narrow = useNarrow() || currentView() === "phone";
  const cancel = async (x: MailQueueItem) => {
    setBusy(x.id);
    try {
      await api("POST", `/mail-queue/${x.id}/cancel`, {});
      toast(M.canceled, undefined, "success", "mail-queue-canceled");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(null);
      await qc.invalidateQueries({ queryKey: MAIL_QUEUE_KEY });
    }
  };
  const action = (x: MailQueueItem) =>
    x.status === "queued" ? (
      <button type="button" className="btn text sm" disabled={busy === x.id} onClick={() => void cancel(x)} aria-label={`${x.documentNo ?? ""} の${M.title}を${M.cancel}`} data-testid="mail-queue-cancel">
        {M.cancel}
      </button>
    ) : x.status === "sent" && x.deliveryId ? (
      <Link className="btn text sm" to={`/deliveries/${x.deliveryId}`} data-testid="mail-queue-open">
        {M.open}
      </Link>
    ) : null;
  const items = q.data?.items ?? [];
  return (
    <section className="paper dlvsec" data-testid="mail-queue">
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {!q.data ? (
        !q.isError ? <p className="hint">{"読み込み中…"}</p> : null
      ) : !items.length ? (
        <p className="hint" data-testid="mail-queue-empty">
          {M.empty}
        </p>
      ) : narrow ? (
        <ul className="dlvcards" data-testid="mail-queue-list">
          {items.map((x) => (
            <li key={x.id} data-testid="mail-queue-row">
              <div className="l1">
                <Link className="num" to={`/invoices/${x.documentId}`}>
                  {x.documentNo ?? "—"}
                </Link>
                <StatusCell x={x} />
                <span className="sp" />
                {action(x)}
              </div>
              <div className="l2">{x.clientName}</div>
              <div className="l3">{`${M.colNext}　${nextText(x)}`}</div>
            </li>
          ))}
        </ul>
      ) : (
        <table className="dlvtable" data-testid="mail-queue-list">
          <thead>
            <tr>
              <th scope="col">{M.colAt}</th>
              <th scope="col">{M.colInvoice}</th>
              <th scope="col">{t(M.colClient)}</th>
              <th scope="col">{M.colStatus}</th>
              <th scope="col">{M.colNext}</th>
              <th scope="col">
                <span className="sr">{M.cancel}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} data-testid="mail-queue-row">
                <td className="num nw">{fmtDateTimeShort(x.createdAt)}</td>
                <td className="num nw">
                  <Link to={`/invoices/${x.documentId}`}>{x.documentNo ?? "—"}</Link>
                </td>
                <td className="to">{x.clientName}</td>
                <td data-testid="mail-queue-status">
                  <StatusCell x={x} />
                </td>
                <td className="num nw">{nextText(x)}</td>
                <td className="r nw">{action(x)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {node}
    </section>
  );
}
